import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Trophy, TrendingUp, Award, Users, Target, Flame, Star, Medal } from 'lucide-react'
import { statsAPI } from '@/lib/api'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'

import PageHeader from '@/components/PageHeader'
export default function StatsPage() {
  const [leaderboard, setLeaderboard] = useState([])
  const [myStats, setMyStats] = useState(null)
  const [achievements, setAchievements] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('leaderboard')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [leaderboardRes, myStatsRes, achievementsRes] = await Promise.all([
        statsAPI.getLeaderboard({ limit: 100 }),
        statsAPI.getMyStats().catch(() => null), // User might not be logged in
        statsAPI.getAchievements()
      ])

      setLeaderboard(leaderboardRes.data.leaderboard || [])
      if (myStatsRes) {
        setMyStats(myStatsRes.data.stats)
      }
      setAchievements(achievementsRes.data.achievements || [])
    } catch (err) {
      console.error('Failed to fetch stats:', err)
      setError(err.response?.data?.error || 'Failed to load stats')
    } finally {
      setLoading(false)
    }
  }

  const getRankBadge = (rank) => {
    if (rank === 1) return <Medal className="h-5 w-5 text-yellow-400" />
    if (rank === 2) return <Medal className="h-5 w-5 text-gray-400" />
    if (rank === 3) return <Medal className="h-5 w-5 text-orange-400" />
    return <span className="text-muted-foreground">#{rank}</span>
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <TrendingUp className="h-12 w-12 animate-pulse mx-auto mb-4 text-blue-400" />
            <p>Loading stats...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <PageHeader icon={TrendingUp} title="Stats" />
        <p className="text-muted-foreground mt-2">
          Track your performance and compete with other players
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-2xl">
          <TabsTrigger value="leaderboard">
            <Trophy className="h-4 w-4 mr-2" />
            Leaderboard
          </TabsTrigger>
          <TabsTrigger value="mystats">
            <Target className="h-4 w-4 mr-2" />
            My Stats
          </TabsTrigger>
          <TabsTrigger value="achievements">
            <Award className="h-4 w-4 mr-2" />
            Achievements
          </TabsTrigger>
        </TabsList>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Top Players</CardTitle>
              <CardDescription>
                Rankings based on total points earned from tournaments and matches
              </CardDescription>
            </CardHeader>
            <CardContent>
              {leaderboard.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>No players ranked yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((player) => (
                    <Link
                      key={player.user_id}
                      to={`/spelers/${player.user_id}`}
                      className={`flex items-center justify-between p-4 rounded-lg border transition-all no-underline hover:border-acid ${
                        player.rank <= 3
                          ? 'border-gold/50 bg-gold/10'
                          : 'border-border bg-card/60'
                      }`}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="flex items-center gap-2 w-12 shrink-0">
                          {getRankBadge(player.rank)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold truncate">
                            {player.display_name || player.username}
                          </div>
                          <div className="text-sm text-muted-foreground truncate">
                            {player.tournaments_won} toernooien gewonnen · {player.matches_won}W-{player.matches_lost}L
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 shrink-0">
                        <div className="text-right">
                          <div className="text-sm text-muted-foreground">Win-rate</div>
                          <div className="font-bold text-live">
                            {player.win_rate?.toFixed(1) || 0}%
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-muted-foreground">Punten</div>
                          <div className="font-bold text-acid">
                            {player.total_points}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-muted-foreground">Badges</div>
                          <div className="font-bold text-gold">
                            {player.achievement_count}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* My Stats Tab */}
        <TabsContent value="mystats" className="space-y-4">
          {!myStats ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12 text-muted-foreground">
                  <Target className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>Login to view your stats</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Trophy className="h-5 w-5 text-blue-400" />
                      <div>
                        <p className="text-sm text-muted-foreground">Total Points</p>
                        <p className="text-2xl font-bold">{myStats.total_points || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Target className="h-5 w-5 text-blue-400" />
                      <div>
                        <p className="text-sm text-muted-foreground">Win Rate</p>
                        <p className="text-2xl font-bold text-green-400">
                          {myStats.win_rate?.toFixed(1) || 0}%
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Flame className="h-5 w-5 text-orange-400" />
                      <div>
                        <p className="text-sm text-muted-foreground">Current Streak</p>
                        <p className="text-2xl font-bold">{myStats.current_streak || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Star className="h-5 w-5 text-yellow-400" />
                      <div>
                        <p className="text-sm text-muted-foreground">Best Streak</p>
                        <p className="text-2xl font-bold">{myStats.best_streak || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Tournament Stats</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tournaments Played:</span>
                      <span className="font-bold">{myStats.tournaments_played || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">1st Place:</span>
                      <span className="font-bold text-yellow-400">{myStats.tournaments_won || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">2nd Place:</span>
                      <span className="font-bold text-gray-400">{myStats.tournaments_second || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">3rd Place:</span>
                      <span className="font-bold text-orange-400">{myStats.tournaments_third || 0}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Match Stats</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Matches Played:</span>
                      <span className="font-bold">{myStats.matches_played || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Wins:</span>
                      <span className="font-bold text-green-400">{myStats.matches_won || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Losses:</span>
                      <span className="font-bold text-red-400">{myStats.matches_lost || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Win Rate:</span>
                      <span className="font-bold text-green-400">
                        {myStats.win_rate?.toFixed(1) || 0}%
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Achievements Tab */}
        <TabsContent value="achievements" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Achievements</CardTitle>
              <CardDescription>
                Unlock achievements by playing tournaments and winning matches
              </CardDescription>
            </CardHeader>
            <CardContent>
              {achievements.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Award className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>No achievements available</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {achievements.map((achievement) => (
                    <div
                      key={achievement.id}
                      className={`p-4 rounded-lg border transition-all ${
                        achievement.earned
                          ? 'border-yellow-500/50 bg-yellow-500/10'
                          : 'border-gray-700 bg-gray-800/30 opacity-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-3xl">{achievement.icon}</div>
                        <div className="flex-1">
                          <div className="font-bold flex items-center gap-2">
                            {achievement.name}
                            {achievement.earned && (
                              <Badge variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
                                Earned
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {achievement.description}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Star className="h-3 w-3 text-yellow-400" />
                            <span className="text-xs text-yellow-400">
                              {achievement.points} points
                            </span>
                          </div>
                          {achievement.earned && achievement.earned_at && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Earned: {new Date(achievement.earned_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
